import React, { useCallback, useMemo, useRef, useState } from 'react';
import {Grid} from '@material-ui/core';
import Button from '../../../components/Button/Button';
import SelectField from '../../../components/SelectField/SelectField';
import {CallStatisticsFormProps, useStyles} from './CallStatistics.utils';
import {useTranslation} from 'react-i18next';
import {DropDownDictionaryItem} from '../ActiveCalls/ActiveCalls.utils';
import { FormikErrors } from 'formik';
import {getDefaultDatePreferences} from '../../Billing/Transactions/utils/DateUtils';
import { ReduxState } from '../../../store/types';
import { useSelector } from 'react-redux';
import { SelectPeriodDialogProps } from './SelectPeriodDialog.utils';
import classNames from 'classnames';
import ExtensionAsyncSelectFiled from '../../../components/CallControls/ExtensionAsyncSelectFiled';
import SelectPeriodDialog from './SelectPeriodDialog';

export type CallStatisticsFiltersProps = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => Promise<FormikErrors<CallStatisticsFormProps>> | Promise<void>,
    errors: FormikErrors<CallStatisticsFormProps>,
    setFieldError: (field: string, value: string | undefined) => void,
    values: CallStatisticsFormProps,
    handleSubmit: (e?: React.FormEvent<HTMLFormElement> | undefined) => void,
    callback?: () => void,
};

export const CallStatisticsFilters: React.VFC<CallStatisticsFiltersProps> = ({
    setFieldValue,
    errors,
    setFieldError,
    values,
    handleSubmit,
    callback
}) => {
    const classes = useStyles();
    const {t} = useTranslation();

    const inputRef = useRef(null);

    const ringGroupsList = useSelector(
        (state: ReduxState) => state.ringgroups?.ringGroupsList ?? [],
    );
    
    const ringGroupsSelectList = useMemo(
        () => {
            const init: DropDownDictionaryItem<string>[] = [];
            init.push({
                name: t('screens:extensions.any'),
                value: '',
            } as DropDownDictionaryItem<string>);
            ringGroupsList?.items?.map(e => {
                init.push({
                    name: e.name,
                    value: e.id
                } as DropDownDictionaryItem<string>)
            });
            return init;
        }, [ringGroupsList],
    );

    const periodsList = useMemo(
        () => {
            const init: DropDownDictionaryItem<number>[] = [];
            init.push({
                name: t('screens:calls.today'),
                value: 0,
            } as DropDownDictionaryItem<number>);
            init.push({
                name: t('screens:calls.yesterday'),
                value: 1,
            } as DropDownDictionaryItem<number>);
            init.push({
                name: t('screens:calls.last7days'),
                value: 2,
            } as DropDownDictionaryItem<number>);
            init.push({
                name: t('screens:calls.last14days'),
                value: 3,
            } as DropDownDictionaryItem<number>);
            init.push({
                name: t('screens:calls.last30days'),
                value: 4,
            } as DropDownDictionaryItem<number>);
            init.push({
                name: t('screens:calls.custom'),
                value: 5,
            } as DropDownDictionaryItem<number>);
            return init;
        }, []);
        
    const selectedPeriod = useMemo(() => {
        const found = periodsList.find(e => e.value === values.period);
        return !!found ? found : null;
    }, [periodsList, values]);

    const [customPeriodDialogOpen, setCustomPeriodDialogOpen] = useState<SelectPeriodDialogProps>({
        isOpen: false,
        from: getDefaultDatePreferences(new Date()).currentMonthStartDate,
        till: getDefaultDatePreferences(new Date()).currentMonthEndDate,
    });

    const selectedRingGroup = useMemo(() => {
        const found = ringGroupsSelectList.find(e => e.value === values.ringGroup);
        return !!found ? found : null;
    }, [ringGroupsSelectList, values]);

    const openCustomDateDialog = useCallback(() => {
        setCustomPeriodDialogOpen({
            ...customPeriodDialogOpen,
            isOpen: true,
            clickOnClose: () => {
                setCustomPeriodDialogOpen({
                    ...customPeriodDialogOpen,
                    isOpen: false
                });
                //@ts-ignore
                inputRef?.current?.focus();
            },
            onUpdate: (fromDate, toDate) => {
                setFieldValue('fromDate', fromDate);
                setFieldValue('toDate', toDate);
                setFieldValue('period', 5);
            }
        });
    }, [customPeriodDialogOpen, setCustomPeriodDialogOpen, inputRef, setFieldValue]);

    return (<Grid item className={classes.itemsContainer}>
                        <Grid item className={classes.inputsContainer}>
                            <SelectField
                                id="period"
                                label={t('common:period')}
                                getOptionLabel={(option: DropDownDictionaryItem<number>) => {
                                    return option.name;
                                }}
                                setFieldError={setFieldError}
                                onChange={(_, option: DropDownDictionaryItem<number>) => {
                                    setFieldValue('period', option.value);
                                    if(option.value == 5) {
                                        openCustomDateDialog();
                                    }
                                }}
                                items={periodsList ?? []}
                                value={selectedPeriod}
                                getOptionSelected={(
                                    option: DropDownDictionaryItem<number>,
                                    value: DropDownDictionaryItem<number>,
                                ) => value.value === option.value}
                                dataQa={'period-select'}
                                skipPermission
                                disableClearable
                                classes={{container: classNames(classes.ringGroupField, classes.dropDownMultiSelect)}}
                                helperText={
                                    errors?.period && errors?.period?.length > 0 ? errors?.period : ''
                                }
                                renderOption={(option: DropDownDictionaryItem<number>) => {
                                    if(option.value === 5) 
                                    {
                                        return (<li value={option.value} onMouseDown={() => {
                                            openCustomDateDialog();
                                        }} style={{width: '100%', height: '100%'}}>
                                            {option.name}
                                        </li>);
                                    }
                                    return (<li value={option.value}>{option.name}</li>);
                                }}
                            />
    
                            <SelectField
                                id="ringGroup"
                                label={t('screens:calls.ringGroup')}
                                getOptionLabel={(option: DropDownDictionaryItem<string>) => {
                                    return option.name;
                                }}
                                setFieldError={setFieldError}
                                onChange={(_, option: DropDownDictionaryItem<string>) => {
                                    setFieldValue('ringGroup', option.value);
                                }}
                                items={ringGroupsSelectList ?? []}
                                value={selectedRingGroup}
                                getOptionSelected={(
                                    option: DropDownDictionaryItem<string>,
                                    value: DropDownDictionaryItem<string>,
                                ) => value.value === option.value}
                                dataQa={'ring-group'}
                                skipPermission
                                disableClearable
                                classes={{container: classNames(classes.ringGroupField, classes.dropDownMultiSelect)}}
                                helperText={
                                    errors?.ringGroup && errors?.ringGroup?.length > 0 ? errors?.ringGroup : ''
                                }
                            />
    
                            <ExtensionAsyncSelectFiled
                                //@ts-ignore
                                value={values.agent}
                                customTitle={t('common:agent')}
                                setValue={(value) => {
                                    setFieldValue('agent', value);
                                }}
                                customClass={classes.extensionField}
                                required={false}
                                useAny={true}
                                showRegisterStatus={false}
                                disableClearable={true}
                                forceEnable={true}
                                useExtensionFilter={true}
                            />
                        </Grid>
                        <Button
                            primary
                            accent
                            dataQa="call-statistics-search"
                            className={classes.button}
                            onClick={() => {
                                handleSubmit();
                                callback?.()
                            }}
                            skipPermission
                        >
                            {t('common:apply')}
                        </Button>
                        <input type="text" ref={inputRef} name="notousedfocus" className={classes.hiddenFocusInput} />
                        <SelectPeriodDialog {...customPeriodDialogOpen}></SelectPeriodDialog>
                    </Grid>
    );
}

export default CallStatisticsFilters;